/** Consumer apex. Clinic is the `pro.` subdomain of this same root — not a new domain. */
export const CONSUMER_HOST = 'getmobilityage.com';
export const CLINIC_PUBLIC_HOST = 'pro.getmobilityage.com';
export const CLINIC_PUBLIC_ORIGIN = 'https://pro.getmobilityage.com';
export const CLINIC_PATH_PREFIX = '/pro';

/**
 * First path segment that must never be a clinic slug.
 * Pro host uses `/{slug}` for clinic home, so these stay reserved.
 */
export const RESERVED_CLINIC_SLUGS = [
  'admin',
  'platform',
  'j',
  'p',
  'people',
  'pro',
  'assets',
  'api',
  'c',
  'dev',
  'setup',
  'audio-test',
  'audio',
  'models',
  'mediapipe',
  'brand',
  'og',
  'index',
  'login',
  'signup',
  'health',
  'clinics',
  'patients',
  'sessions',
  'reminders',
  'staff',
  'today',
  'due',
  'calendar',
  'visits',
  'billing',
  'manifest',
  'robots',
  'favicon',
  'sw',
] as const;

export type ReservedClinicSlug = (typeof RESERVED_CLINIC_SLUGS)[number];

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 48;

export type ClinicRoute =
  | { name: 'home' }
  | { name: 'admin' }
  | { name: 'clinic'; slug: string }
  | { name: 'join'; token: string }
  | { name: 'pin'; code: string }
  | { name: 'patient'; id: string };

function stripTrailingSlash(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function hostnameOf(host: string): string {
  return host.toLowerCase().split(':')[0] ?? host.toLowerCase();
}

export function isLocalDevHost(hostname: string): boolean {
  const host = hostnameOf(hostname);
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.pages.dev') ||
    host.endsWith('.vercel.app')
  );
}

/**
 * Clinic public host is only pro.getmobilityage.com (same root as consumer).
 * Not mymobilityage.com, not a bare `pro.` prefix on some other domain.
 */
export function isClinicHost(hostname: string): boolean {
  return hostnameOf(hostname) === CLINIC_PUBLIC_HOST;
}

export function isReservedClinicSlug(slug: string): boolean {
  return (RESERVED_CLINIC_SLUGS as readonly string[]).includes(slug);
}

export function isValidClinicSlug(slug: string): boolean {
  return (
    slug.length >= SLUG_MIN_LENGTH &&
    slug.length <= SLUG_MAX_LENGTH &&
    SLUG_PATTERN.test(slug)
  );
}

/** Lowercase URL-safe slug. Empty if nothing usable remains. */
export function slugifyClinicName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH);
}

export function normalizeClinicSlug(raw: string): string {
  return slugifyClinicName(raw);
}

/** Human error if this slug cannot be used. Null when the format is ok. */
export function clinicSlugFormatError(slug: string): string | null {
  if (isReservedClinicSlug(slug)) {
    return '"' + slug + '" is reserved. Pick another link.';
  }
  if (!isValidClinicSlug(slug)) {
    return 'Use lowercase letters, numbers, and hyphens.';
  }
  return null;
}

function isClinicRootPath(pathname: string): boolean {
  const path = stripTrailingSlash(pathname);
  return (
    path === '/admin' ||
    path === '/platform' ||
    /^\/j\/[a-zA-Z0-9]{6,16}$/.test(path) ||
    /^\/p\/[a-zA-Z0-9]{4,12}$/.test(path) ||
    /^\/people\/[a-zA-Z0-9_-]+$/.test(path)
  );
}

export function isClinicPath(pathname: string): boolean {
  const path = stripTrailingSlash(pathname);
  return (
    path === CLINIC_PATH_PREFIX ||
    path.startsWith(CLINIC_PATH_PREFIX + '/') ||
    isClinicRootPath(path)
  );
}

const CLINICAL_PREVIEW_REST = '/dev/clinical-ui';

/** Fixture screens for Pro clinical scores (session + person card). */
export function isClinicalPreviewPath(pathname: string): boolean {
  const path = stripTrailingSlash(pathname);
  return (
    path === CLINICAL_PREVIEW_REST ||
    path === CLINIC_PATH_PREFIX + CLINICAL_PREVIEW_REST
  );
}

export function isClinicEntry(loc: {
  hostname: string;
  pathname: string;
}): boolean {
  return isClinicHost(loc.hostname) || isClinicPath(loc.pathname);
}

/** True while the Pro/clinic app is the one on screen. */
export function isClinicMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isClinicEntry(window.location);
}

export function clinicBasePath(loc: {
  hostname: string;
  pathname?: string;
}): string {
  return isClinicHost(loc.hostname) ? '' : CLINIC_PATH_PREFIX;
}

export function parseClinicRoute(
  pathname: string,
  loc: { hostname: string } = { hostname: '' }
): ClinicRoute {
  const base = clinicBasePath(loc);
  let rest = stripTrailingSlash(pathname);
  if (base && (rest === base || rest.startsWith(base + '/'))) {
    rest = rest.slice(base.length) || '/';
  }
  const join = rest.match(/^\/j\/([a-zA-Z0-9]{6,16})$/);
  if (join?.[1]) {
    return { name: 'join', token: join[1] };
  }
  const pin = rest.match(/^\/p\/([a-zA-Z0-9]{4,12})$/);
  if (pin?.[1]) {
    return { name: 'pin', code: pin[1].toUpperCase() };
  }
  const patient = rest.match(/^\/people\/([a-zA-Z0-9_-]+)$/);
  if (patient?.[1]) {
    return { name: 'patient', id: patient[1] };
  }
  if (rest === '/admin' || rest === '/platform') {
    return { name: 'admin' };
  }
  const clinic = rest.match(/^\/([a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*)$/);
  if (clinic?.[1]) {
    const slug = clinic[1].toLowerCase();
    if (isValidClinicSlug(slug) && !isReservedClinicSlug(slug)) {
      return { name: 'clinic', slug };
    }
  }
  return { name: 'home' };
}

export function clinicPath(
  route: ClinicRoute,
  loc: { hostname: string }
): string {
  const base = clinicBasePath(loc);
  if (route.name === 'join') {
    return base + '/j/' + route.token;
  }
  if (route.name === 'pin') {
    return base + '/p/' + route.code;
  }
  if (route.name === 'patient') {
    return base + '/people/' + route.id;
  }
  if (route.name === 'admin') {
    return (base || '') + '/admin';
  }
  if (route.name === 'clinic') {
    return (base || '') + '/' + route.slug;
  }
  return base || '/';
}

export function clinicOrigin(loc: {
  origin: string;
  hostname: string;
}): string {
  return loc.origin.replace(/\/$/, '');
}

/**
 * Public join/PIN links always target the Pro subdomain in production.
 * Localhost and preview deploys keep the current origin + /pro fallback.
 */
export function clinicLinkOrigin(loc: {
  origin: string;
  hostname: string;
}): { origin: string; hostname: string } {
  if (isClinicHost(loc.hostname) || isLocalDevHost(loc.hostname)) {
    return { origin: clinicOrigin(loc), hostname: hostnameOf(loc.hostname) };
  }
  return { origin: CLINIC_PUBLIC_ORIGIN, hostname: CLINIC_PUBLIC_HOST };
}

export function joinUrl(
  token: string,
  loc: { origin: string; hostname: string }
): string {
  const target = clinicLinkOrigin(loc);
  return target.origin + clinicPath({ name: 'join', token }, target);
}

export function pinEntryUrl(
  code: string,
  loc: { origin: string; hostname: string }
): string {
  const target = clinicLinkOrigin(loc);
  return target.origin + clinicPath({ name: 'pin', code }, target);
}
